import { useRef, useState } from 'react';

import FormikCustomInput from '@perpay-web/components/base/FormikCustomInput/FormikCustomInput';
import {
    isEnterOrSpaceKey,
    KeyboardKey,
} from '@perpay-web/utils/keyboardUtils';
import { noop } from '@perpay-web/utils/noop';

import styles from './Combobox.scss';

/**
 * Implements a WAI-ARIA compliant combo-box widget with manual selection.
 * https://www.w3.org/TR/wai-aria-practices/examples/combobox/aria1.1pattern/listbox-combo.html
 */
const Combobox = ({
    children,
    expanded,
    getValueAtIndex,
    highlightedIndex = null,
    id,
    inputValue,
    label,
    loading,
    name,
    onArrowDown,
    onArrowUp,
    onEscape,
    onHighlightIndex,
    onInputValueChange,
    onSelect,
    placeholder,
    selectedItem = null,
    inputComponent: InputComponent = FormikCustomInput,
}) => {
    const renderFunc = children;
    const inputRef = useRef();

    return (
        <div className={styles.combobox}>
            <div className={styles.comobox__field}>
                <InputComponent
                    ref={inputRef}
                    label={label}
                    type='text'
                    field={{
                        'aria-activedescendant':
                            typeof highlightedIndex === 'number'
                                ? `${id}-option-${highlightedIndex}`
                                : null,
                        'aria-autocomplete': 'list',
                        'aria-controls': `${id}-listbox`,
                        'aria-expanded': expanded,
                        'aria-haspopup': 'listbox',
                        'aria-live': 'polite',
                        'aria-owns': `${id}-listbox`,
                        name,
                        onChange: (e) => {
                            onInputValueChange(e.target.value);
                        },
                        onKeyDown: (event) => {
                            switch (event.key) {
                                case KeyboardKey.ARROW_DOWN:
                                    onArrowDown(highlightedIndex);
                                    break;
                                case KeyboardKey.ARROW_UP:
                                    onArrowUp(highlightedIndex);
                                    break;
                                case KeyboardKey.ESCAPE:
                                    onEscape();
                                    break;
                                case KeyboardKey.ENTER: {
                                    const highlightedItem =
                                        getValueAtIndex(highlightedIndex);
                                    onSelect(
                                        highlightedItem && highlightedItem.text,
                                    );
                                    break;
                                }
                                default:
                                    break;
                            }
                        },
                        placeholder,
                        role: 'combobox',
                        value: inputValue,
                    }}
                />
                {loading ? (
                    <div className={styles.combobox__search__loader} />
                ) : null}
            </div>
            <div
                hidden={!expanded}
                role='listbox'
                id={`${id}-listbox`}
                className={styles.combobox__results}
            >
                {renderFunc({
                    onHighlightIndex,
                    onSelect,
                    highlightedIndex,
                    selectedItem,
                    getItemId: (index) => `${id}-option-${index}`,
                    getHighlightedCss: () =>
                        styles['combobox__result--highlighted'],
                    getSelectedCss: () => styles['combobox__result--selected'],
                    getResultCss: () => styles.combobox__result,
                })}
            </div>
        </div>
    );
};

/**
 * Returns a React Component that renders the combobox results  and is bound to
 * the given items array. DefaultComboboxResults renders the results list with
 * sane defaults so that consumers of Combobox do not need to implement the results
 * themselves for a simple array of items. e.g.
 * ```
 * <Combobox ...>
 * {defaultRender(results)}
 * </Combobox>
 * ```
 *
 * For more complex results data, a custom
 * component should be created by the consumer of Combobox and passed as the
 * children prop, e.g.
 * ```
 * <Combobox ...>
 * {({
 *      getHighlightedCss,
 *      getItemId,
 *      getResultCss,
 *      getSelectedCss,
 *      highlightedIndex,
 *      onHighlightIndex,
 *      onSelect,
 *      selectedItem,
 * }) => (
 *      <ul>complexResults.map(...)</ul>
 * )}
 * </Combobox>
 * ```
 * @knipignore Exported for testing only
 */
export function defaultRender(items) {
    const DefaultComboboxResults = ({
        getHighlightedCss,
        getItemId,
        getResultCss,
        getSelectedCss,
        highlightedIndex = null,
        onHighlightIndex,
        onSelect,
        selectedItem = null,
    }) => (
        <ul>
            {items.map((item, index) => (
                <li
                    id={getItemId(index)}
                    key={item.text}
                    className={
                        getResultCss() +
                        (highlightedIndex === index
                            ? ` ${getHighlightedCss()}`
                            : '') +
                        (selectedItem === item.text
                            ? ` ${getSelectedCss()}`
                            : '')
                    }
                    role='option'
                    aria-selected={
                        item ? item.value === highlightedIndex : null
                    }
                    onMouseOver={() => onHighlightIndex(index)}
                    onClick={() => {
                        onSelect(item.text);
                    }}
                    // This onKeyDown shouldn't get called, but the
                    // linter requires it. It won't get called because
                    // the focus remains on the input above while the
                    // user keys down. If we give these tabindex="0"
                    // then this is needed.
                    onKeyDown={(event) => {
                        if (isEnterOrSpaceKey(event.key)) {
                            onSelect(item.text);
                        }
                    }}
                    onFocus={() => {
                        onHighlightIndex(index);
                    }}
                >
                    {item.text}
                </li>
            ))}
        </ul>
    );

    return DefaultComboboxResults;
}

const StatefulCombobox = ({
    initialValue,
    onInputValueChange = noop,
    itemCount,
    onSelect = noop,
    getValueAtIndex,
    children,
    label = '',
    id,
    loading = false,
    placeholder = '',
    inputComponent,
}) => {
    const [expanded, setExpanded] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(null);
    const [inputValue, setInputValue] = useState(initialValue);
    const [selectedItem, setSelectedItem] = useState(initialValue);

    const onHighlightIndex = (index) => {
        setHighlightedIndex(index);
    };

    const onInputValueChangeInternal = (value) => {
        setExpanded(true);
        setHighlightedIndex(null);
        setInputValue(value);
        setSelectedItem(null);
        onInputValueChange(value);
    };

    const onArrowDown = (currentIndex) => {
        const baseIndex = currentIndex === null ? -1 : currentIndex;
        setExpanded(true);
        setHighlightedIndex((baseIndex + 1) % itemCount);
    };

    const onArrowUp = (currentIndex) => {
        const baseIndex = currentIndex === null ? itemCount : currentIndex;
        const newIndex = baseIndex - 1;
        setExpanded(true);
        setHighlightedIndex(newIndex < 0 ? itemCount - 1 : newIndex);
    };

    const onEscape = () => {
        setExpanded(false);
    };

    const onSelectInternal = (value, valueToSet = '') => {
        if (!value) {
            return;
        }

        onSelect(value);
        setExpanded(false);
        setSelectedItem(value);
        if (valueToSet) {
            setInputValue(valueToSet);
        } else if (value) {
            setInputValue(value);
        }
    };

    return (
        <Combobox
            expanded={expanded}
            getValueAtIndex={getValueAtIndex}
            highlightedIndex={highlightedIndex}
            id={id}
            inputValue={inputValue}
            label={label}
            loading={loading}
            name={id}
            onArrowDown={(...args) => onArrowDown(...args)}
            onArrowUp={(...args) => onArrowUp(...args)}
            onEscape={(...args) => onEscape(...args)}
            onHighlightIndex={(...args) => onHighlightIndex(...args)}
            onInputValueChange={(...args) =>
                onInputValueChangeInternal(...args)
            }
            onSelect={(...args) => onSelectInternal(...args)}
            placeholder={placeholder}
            selectedItem={selectedItem}
            inputComponent={inputComponent}
        >
            {children}
        </Combobox>
    );
};

export default StatefulCombobox;
